import * as React from "react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

export interface TruncatedTextProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /**
   * The label. Clipped to one line, and shown whole in the tooltip. Rich
   * content is allowed; the tooltip then carries the text it renders to.
   */
  children: React.ReactNode
  /** Which side the tooltip opens on. Defaults to the house default (top). */
  side?: "top" | "right" | "bottom" | "left"
}

/**
 * Single-line content that ends in an ellipsis when it outgrows its box, and
 * reveals the whole string in a tooltip on hover. The tooltip only arms itself
 * while the text is actually clipped, so short values stay quiet.
 *
 * Sized by its container: give the parent a width (and `min-w-0` inside a flex
 * or grid item, whose automatic minimum size otherwise refuses to shrink) and
 * this fills it.
 */
const TruncatedText = ({
  children,
  className,
  side,
  ...props
}: TruncatedTextProps) => {
  const textRef = React.useRef<HTMLSpanElement | null>(null)
  // The text the box had to cut off, or null while it all fits. Read off the
  // node rather than from `children`, so rich content reveals the same string
  // the user sees clipped.
  const [clippedText, setClippedText] = React.useState<string | null>(null)

  React.useLayoutEffect(() => {
    const node = textRef.current
    if (!node) {
      return
    }
    // Both widths are rounded to integers, so a subpixel layout can report a
    // scroll width one larger than the client width with nothing clipped.
    const measure = () => {
      setClippedText(
        node.scrollWidth > node.clientWidth + 1 ? node.textContent : null,
      )
    }
    measure()
    if (typeof ResizeObserver === "undefined") {
      return
    }
    // Catches the box changing under fixed text — a column resize, a sidebar
    // opening. Text or styling changing under a fixed box re-runs the effect
    // instead, since neither necessarily resizes the span.
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [children, className])

  return (
    <Tooltip
      content={clippedText}
      side={side}
      openOnInteraction={clippedText !== null}
    >
      <span
        ref={textRef}
        className={cn("block min-w-0 truncate", className)}
        {...props}
      >
        {children}
      </span>
    </Tooltip>
  )
}

export { TruncatedText }
